import { DropDown, type DropDownResult, type JsonData } from '@myosh/odin-components'
import { useMemo } from 'react'
import { filtersActions as actions, filtersActions } from '../../../../redux/actions'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { type DateFieldOption, DateOptions, dateFieldOptions } from '../../../../redux/types/data-options'
import Checkbox, { type CheckboxChangeEvent } from '../../../UI/checkbox/Checkbox'
import SimpleInput from '../../../UI/simple-input/SimpleInput'

export const defaultDateFieldOption = dateFieldOptions[0]

const DisplayOptionsTM = () => {
  // State
  const dispatch = useAppDispatch()
  const trainingMatrixFilters = useAppSelector(state => state.trainingMatrixFilters)
  const trainingMatrixState = useAppSelector(state => state.trainingMatrix)

  // Computed props
  const dateOptions: DateFieldOption[] = useMemo(() => {
    if (!trainingMatrixState.hasInTrainingStatus) {
      return dateFieldOptions.filter(options => options.value !== String(DateOptions.Started))
    }

    return dateFieldOptions
  }, [trainingMatrixState.hasInTrainingStatus])

  let selectedDisplayDate = trainingMatrixFilters.selectedDisplayDate
  if (
    trainingMatrixFilters.showDates === true &&
    trainingMatrixFilters.exportFormat !== 'ICONS' &&
    trainingMatrixFilters.selectedDisplayDate.exportFormat !== trainingMatrixFilters.exportFormat
  ) {
    selectedDisplayDate =
      dateOptions.find(dateOption => dateOption.exportFormat === trainingMatrixFilters.exportFormat) ??
      trainingMatrixFilters.selectedDisplayDate
  }

  // Handlers
  const handleViewModeChange = (selection?: CheckboxChangeEvent) => {
    dispatch(actions.setSelectedDisplayViewMode(selection?.checked ?? false))
  }
  const handleDateChange = (selection: unknown) => {
    const originalData = (selection as DropDownResult).originalData
    dispatch(actions.setSelectedDisplayDate(originalData as unknown as DateFieldOption))
  }
  const handleGridlineFrequencyChange = (frequency: string | number) => {
    dispatch(actions.setGridlinesFrequency(Number(frequency)))
  }
  const handleFixGridlineFrequency = () => {
    if (trainingMatrixFilters.gridlinesFrequency > 10) {
      dispatch(actions.setGridlinesFrequency(10))
    } else if (trainingMatrixFilters.gridlinesFrequency < 1) {
      dispatch(actions.setGridlinesFrequency(1))
    }
  }
  const handleChangeSlantHeaders = (selection?: CheckboxChangeEvent) => {
    dispatch(actions.setSlantHeaders(selection?.checked ?? false))
  }

  const toggleShowGroups = (selection?: CheckboxChangeEvent) => {
    dispatch(actions.setShowGroups(selection?.checked ?? false))
  }
  const toggleShowGridlines = (selection?: CheckboxChangeEvent) => {
    dispatch(actions.setShowGridSeparator(selection?.checked ?? false))
  }

  const handleLimitChange = (limit: string | number) => {
    dispatch(filtersActions.setExpirationDateLimit(Number(limit)))
  }

  const handleLimitOnBlur = (limit?: string | number) => {
    const numLimit = Number(limit)
    if (isNaN(numLimit) || numLimit < 1) {
      dispatch(filtersActions.setExpirationDateLimit(30))
    }
  }

  const handleColourBlindThemeChange = (value?: CheckboxChangeEvent) => {
    dispatch(filtersActions.setIsColourBlindThemeEnabled(value?.checked ?? false))
    document.documentElement.classList.toggle('colour-blind')
  }

  return (
    <>
      <div style={{ marginRight: '4rem' }}>
        <Checkbox
          label='Show Competencies Groups'
          name='showGroups'
          onChange={toggleShowGroups}
          checked={trainingMatrixFilters.showGroups}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
        <Checkbox
          label='Show Gridlines'
          name='showGridLines'
          onChange={toggleShowGridlines}
          checked={trainingMatrixFilters.showGridSeparator}
        />
        {trainingMatrixFilters.showGridSeparator && (
          <div style={{ width: '4.25rem' }}>
            <SimpleInput
              label='Frequency'
              min={1}
              max={10}
              onInput={handleGridlineFrequencyChange}
              onBlur={handleFixGridlineFrequency}
              type={'number'}
              value={trainingMatrixFilters.gridlinesFrequency}
            />
          </div>
        )}
      </div>
      <div style={{ marginRight: '4rem' }}>
        <Checkbox
          name='showDate'
          label='Show Dates'
          onChange={handleViewModeChange}
          checked={trainingMatrixFilters.showDates}
        />
      </div>
      {trainingMatrixFilters.showDates && (
        <div style={{ width: '10rem', marginBottom: '1rem' }}>
          <DropDown
            allowClear={false}
            data={dateOptions as unknown as JsonData}
            value={selectedDisplayDate}
            label='Date'
            placeholder='Select date'
            textField='text'
            valueField='value'
            onChange={handleDateChange}
          />
        </div>
      )}
      <Checkbox
        name='slant'
        label='Angle Competency Names'
        onChange={handleChangeSlantHeaders}
        checked={trainingMatrixFilters.slantHeaders}
      />
      <Checkbox
        label='Enable Colourblind Theme'
        checked={trainingMatrixFilters.isColourBlindThemeEnabled}
        onChange={handleColourBlindThemeChange}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
        <span style={{ fontSize: '0.875rem' }}>Expiration limit (days)</span>
        <div style={{ width: '4.25rem' }}>
          <SimpleInput
            label='Limit'
            type='number'
            min={1}
            onInput={handleLimitChange}
            onBlur={handleLimitOnBlur}
            value={trainingMatrixFilters.expirationDateLimit}
          />
        </div>
      </div>
    </>
  )
}

export default DisplayOptionsTM
